'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useReCaptcha } from 'next-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useHelpWidget } from '../HelpWidgetContext';
import submitContactFormAction from '../actions/contactFormAction';
const contactSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().min(1, 'Email is required').email('Please enter a valid email'),
  subject: z.string().min(1, 'Subject is required'),
  message: z.string().min(1, 'Message is required')
});
const HelpContactForm = () => {
  const {
    setPanel
  } = useHelpWidget();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [serverError, setServerError] = useState(null);
  const {
    executeRecaptcha
  } = useReCaptcha();
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isSubmitting
    }
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: ''
    }
  });
  const onSubmit = async data => {
    setServerError(null);
    try {
      const token = await executeRecaptcha('contact_form');
      const response = await submitContactFormAction({
        ...data,
        recaptcha_token: token
      });
      if (response.success) {
        setIsSubmitted(true);
      } else {
        setServerError(response.message || 'An error occurred. Please try again.');
      }
    } catch (err) {
      setServerError('An error occurred. Please try again.');
    }
  };
  if (isSubmitted) {
    return <div className="p-4">
                <h3 className="text-md font-semibold mb-3">Thank you.</h3>
                <p className="text-sm mb-4">
                    We have received your message and will respond shortly.
                </p>
                <button onClick={() => setPanel('main')} className="text-sm text-primary hover:underline">
                    return to help menu
                </button>
                <ContactInfo />
            </div>;
  }
  return <div className="p-4">
            <div className="text-md font-semibold mb-2">Contact hive</div>
            <p className="text-sm text-light mb-4">
                We'd love to hear from you. Complete the form below and we'll respond promptly, or call us toll free at{' '}
                <a href="tel:+18666634483" className="text-primary hover:underline">1.866.663.4483</a>.
            </p>

            <form onSubmit={handleSubmit(onSubmit)}>
                {serverError && <div className="text-red text-sm mb-3">{serverError}</div>}

                <div className="form-group mb-3">
                    <input id="contact-name" type="text" autoComplete="name" className={`form-input form-input-sm peer ${errors.name ? 'border-red' : ''}`} placeholder=" " disabled={isSubmitting} aria-invalid={!!errors.name} {...register('name')} />
                    <label htmlFor="contact-name" className="form-input-label form-input-label-sm peer-placeholder-shown:form-input-label-placeholder-shown">
                        your full name <span className="text-primary">*</span>
                    </label>
                    {errors.name && <div className="text-red text-2xs mt-1">{errors.name.message}</div>}
                </div>

                <div className="form-group mb-3">
                    <input id="contact-email" type="email" autoComplete="email" className={`form-input form-input-sm peer ${errors.email ? 'border-red' : ''}`} placeholder=" " disabled={isSubmitting} aria-invalid={!!errors.email} {...register('email')} />
                    <label htmlFor="contact-email" className="form-input-label form-input-label-sm peer-placeholder-shown:form-input-label-placeholder-shown">
                        email <span className="text-primary">*</span>
                    </label>
                    {errors.email && <div className="text-red text-2xs mt-1">{errors.email.message}</div>}
                </div>

                <div className="form-group mb-3">
                    <input id="contact-subject" type="text" autoComplete="off" className={`form-input form-input-sm peer ${errors.subject ? 'border-red' : ''}`} placeholder=" " disabled={isSubmitting} aria-invalid={!!errors.subject} {...register('subject')} />
                    <label htmlFor="contact-subject" className="form-input-label form-input-label-sm peer-placeholder-shown:form-input-label-placeholder-shown">
                        subject <span className="text-primary">*</span>
                    </label>
                    {errors.subject && <div className="text-red text-2xs mt-1">{errors.subject.message}</div>}
                </div>

                <div className="form-group mb-4">
                    <textarea id="contact-message" className={`form-input form-input-sm peer ${errors.message ? 'border-red' : ''}`} placeholder=" " rows="3" disabled={isSubmitting} aria-invalid={!!errors.message} {...register('message')} />
                    <label htmlFor="contact-message" className="form-input-label form-input-label-sm peer-placeholder-shown:form-input-label-placeholder-shown">
                        additional information (order number, query) <span className="text-primary">*</span>
                    </label>
                    {errors.message && <div className="text-red text-2xs mt-1">{errors.message.message}</div>}
                </div>

                <button type="submit" disabled={isSubmitting} className={`btn btn-primary btn-sm w-full ${isSubmitting ? 'opacity-50' : ''}`}>
                    {isSubmitting ? 'submitting...' : 'leave a message'}
                </button>
            </form>
        </div>;
};
const ContactInfo = () => {
  const $ = _c(6);
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="text-primary font-medium">hive</div>;
    t1 = <div>820 nw glisan street</div>;
    t2 = <div>portland or 97209</div>;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div>tele: <a href="tel:+15032421967" className="hover:text-primary">503.242.1967</a></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div>fax: <a href="tel:+15032425573" className="hover:text-primary">503.242.5573</a></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="text-sm text-light mt-4 pt-4 border-t border-gray-10">{t0}{t1}{t2}{t3}{t4}<div>toll-free: <a href="tel:+18666634483" className="hover:text-primary">1.866.663.4483</a></div><div><a href="mailto:info@hivemodern.com" className="text-primary hover:underline">info@hivemodern.com</a></div></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};
export default HelpContactForm;